import { t } from 'i18next'

import {
  formatDateExibitionUTC,
  formatMoney,
  formatNumberWithDots,
  formatPercent,
  isPublisher
} from '~/helpers'
import { RoasMetricCell } from '~/modules/retailMedia/components/RoasMetricCell'
import {
  buildNtbColumns,
  buildNtbTooltipValues,
  NtbMetric
} from '~/modules/retailMedia/helpers/ntbMetrics'

import { ImpressionShareMetricCell } from '../_tables/_elements/ImpressionShareMetricCell'

// dia sem snapshot vem null e vira traço, nunca 0
const formatNtbCell = (metric: NtbMetric, value: unknown) => {
  if (value == null) return '-'

  if (metric.format === 'unit') return formatNumberWithDots(Number(value))
  if (metric.format === 'currency') return formatMoney(Number(value))

  return formatPercent(value)
}

type GetSchemaProps = {
  campaignType: CampaignAdType
  availableMetrics?: NewMetricsAvailability
  lookbackMonths?: number
  ntbOrdersWindow?: NtbOrdersWindow
}

const avgCpmColumn = {
  avg_cpm: {
    title: t('rm:metrics.cpm'),
    helpText: t('rm:metrics.cpm.tooltip'),
    render: rowData =>
      rowData?.avg_cpm ? formatMoney(Number(rowData?.avg_cpm)) : '-'
  }
}

const avgCost = {
  product: {
    avg_cpc: {
      title: t('rm:metrics.cpc'),
      helpText: t('rm:metrics.cpc.tooltip'),
      render: rowData =>
        rowData?.avg_cpc ? formatMoney(Number(rowData?.avg_cpc)) : '-'
    }
  },
  banner: avgCpmColumn,
  // Same effective CPM (avg_cpm) column as onsite banner, for every offsite
  // banner row regardless of cost_type (standard or total_budget) — the
  // daily history table always shows the per-day calculated CPM, not a bid.
  banner_off_site: avgCpmColumn
}

/**
 * Esta função espera o tipo da campanha para saber quais campos deve retornar no schema.
 * Hoje trata apenas custo médio de CPC e CPM
 */
export const getSchema = ({
  campaignType,
  availableMetrics = {
    impressionShare: false,
    lostIsRank: false,
    newToBrandCustomers: false,
    newToBrandRevenue: false
  },
  lookbackMonths,
  ntbOrdersWindow
}: GetSchemaProps) => {
  return {
    data: {
      title: t('rm:Date'),
      render: rowData => formatDateExibitionUTC(rowData?.groupby)
    },
    impressions: {
      title: t('rm:metrics.impressions'),
      render: rowData => formatNumberWithDots(rowData.impressions)
    },
    clicks: {
      title: t('rm:metrics.clicks'),
      render: rowData => formatNumberWithDots(rowData.clicks)
    },
    conversions: {
      title: t('rm:metrics.conversions'),
      render: rowData => formatNumberWithDots(rowData.conversions)
    },
    ctr: {
      title: t('rm:metrics.ctr'),
      helpText: t('rm:metrics.ctr.tooltip'),
      render: rowData => formatPercent(rowData?.ctr)
    },
    conversionRate: {
      title: t('rm:metrics.conversion_rate'),
      render: rowData => formatPercent(rowData?.conversion_rate)
    },
    ...avgCost[campaignType],
    cpa: {
      title: t('rm:metrics.cpa'),
      helpText: t('rm:metrics.cpa.tooltip'),
      render: rowData => formatMoney(Number(rowData?.cpa))
    },
    cost: {
      title: isPublisher()
        ? t('rm:metrics.adsRevenue')
        : t('rm:metrics.investment'),
      render: rowData => formatMoney(Number(rowData?.total_spent))
    },
    income: {
      title: t('rm:metrics.income'),
      render: rowData => formatMoney(Number(rowData?.income))
    },
    roas: {
      title: t('rm:metrics.roas'),
      helpText: t('rm:metrics.roas.tooltip'),
      render: rowData => (
        <RoasMetricCell
          metric="roas"
          value={formatNumberWithDots(rowData?.roas)}
          raw={rowData}
        />
      )
    },
    ...(availableMetrics.impressionShare && {
      impressionShare: {
        title: t('rm:metrics.impressionShare'),
        helpText: t('rm:metrics.impressionShare.tooltip'),
        render: rowData => (
          <ImpressionShareMetricCell
            value={
              rowData?.impression_share == null
                ? '-'
                : formatPercent(rowData.impression_share)
            }
            raw={rowData}
          />
        )
      }
    }),
    ...(availableMetrics.lostIsRank && {
      lostIsRank: {
        title: t('rm:metrics.lostIsRank'),
        helpText: t('rm:metrics.lostIsRank.tooltip'),
        optional: true,
        render: rowData =>
          rowData?.lost_is_rank == null
            ? '-'
            : formatPercent(rowData.lost_is_rank)
      }
    }),
    // sem sortKey: a série diária não ordena pela API
    ...buildNtbColumns<ReportDayItem>({
      availableMetrics,
      ntbValues: buildNtbTooltipValues({
        lookbackMonths,
        ordersWindow: ntbOrdersWindow
      }),
      sortable: false,
      render: metric => rowData =>
        formatNtbCell(metric, rowData?.[metric.rawKey])
    })
  } satisfies TableSchema
}
